import { type SnapshotDay, snapshotDayLabel } from "@healthos/shared";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { useState } from "react";

/** A week reads at a glance; the fortnight is one tap away. */
const TIMELINE_PREVIEW_DAYS = 7;

/**
 * The drill's recent days in the order they happened, newest first. The chart
 * shows the shape; this says what each day actually was, with whatever else was
 * true that day on the same row. Nothing here claims one caused the other.
 */
export function SnapshotTimeline({
  days,
  label,
}: {
  days: ReadonlyArray<SnapshotDay>;
  label: string;
}) {
  const [all, setAll] = useState(false);
  const reported = days.filter((d) => d.display !== null).length;
  const shown = all ? days : days.slice(0, TIMELINE_PREVIEW_DAYS);
  if (reported === 0) return null;
  return (
    <section className="snap-card snap-tl">
      <div className="snap-section-head">
        <h3>Day by day</h3>
        <span className="hint">{label}, newest first</span>
      </div>
      <ol className="snap-tl-list">
        {shown.map((d, i) => (
          <li key={d.day} className={`snap-tl-day${d.display === null ? " is-quiet" : ""}`}>
            <span className="snap-tl-when">{snapshotDayLabel(d.day, i)}</span>
            <span className={`snap-tl-v${d.verdict ? ` is-${d.verdict}` : ""}`}>
              {d.display ?? "Nothing reported"}
            </span>
            <span className="snap-tl-ctx">
              {d.context.length > 0
                ? d.context.map((c) => (
                    <b key={c} className="snap-tl-mark">
                      {c}
                    </b>
                  ))
                : null}
            </span>
          </li>
        ))}
      </ol>
      {days.length > TIMELINE_PREVIEW_DAYS ? (
        <button
          className="snap-tl-more"
          type="button"
          aria-expanded={all}
          onClick={() => setAll((v) => !v)}
        >
          {all ? `Show ${TIMELINE_PREVIEW_DAYS} days` : `Show all ${days.length} days`}
          {all ? (
            <CaretUp size={12} aria-hidden="true" />
          ) : (
            <CaretDown size={12} aria-hidden="true" />
          )}
        </button>
      ) : null}
    </section>
  );
}
